import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, API as API_BASE } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useBranding } from "@/lib/branding";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import {
  ArrowRight,
  Award,
  BookOpen,
  Check,
  Download,
  FileCheck2,
  Highlighter,
  ListOrdered,
  RotateCcw,
  X,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function AfspPrep() {
  const { user, loading } = useAuth();
  const { branding } = useBranding();
  const nav = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [status, setStatus] = useState(null);
  const [sections, setSections] = useState([]);
  const [pdfMode, setPdfMode] = useState("annotated"); // "plain" | "annotated"
  const [pdfPage, setPdfPage] = useState(1);
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login?next=/afsp-prep"); return; }
    api.get("/afsp/status")
      .then((r) => {
        setStatus(r.data);
        if (r.data?.has_access) {
          api.get("/afsp/quiz").then((q) => setQuestions(q.data.questions || []));
          api.get("/afsp/study-sections").then((s) => setSections(s.data.sections || []));
        }
      })
      .catch(() => setStatus({ has_access: false }));
  }, [user, loading, nav]);

  const answered = Object.keys(answers).length;
  const totalQ = questions.length;
  const pct = totalQ ? Math.round((answered / totalQ) * 100) : 0;

  const pick = (qid, idx) => setAnswers((prev) => ({ ...prev, [qid]: idx }));

  const submit = async () => {
    if (answered < totalQ) {
      toast.warning(`You still have ${totalQ - answered} question(s) to answer.`);
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post("/afsp/quiz/submit", { answers });
      setResult(r.data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit quiz");
    } finally { setSubmitting(false); }
  };

  const resetQuiz = () => {
    setAnswers({});
    setResult(null);
  };

  const downloadPdf = async (variant = pdfMode) => {
    try {
      const token = localStorage.getItem("tpa_token");
      const path = variant === "annotated" ? "/api/afsp/download-annotated" : "/api/afsp/download-guide";
      const resp = await fetch(`${API}${path}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) {
        const t = await resp.text();
        toast.error(t || "Could not download study guide");
        return;
      }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = variant === "annotated"
        ? "IRS-Circular-230-Annotated-Study-Guide.pdf"
        : "IRS-Circular-230-Study-Guide.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Study guide download started.");
    } catch (e) {
      toast.error("Download failed — please retry.");
    }
  };

  // Auth-injected PDF viewer URL (blob-based so we can pass Authorization header).
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  useEffect(() => {
    if (!status?.has_access || !pdfViewerOpen) return;
    let revoked = null;
    (async () => {
      const token = localStorage.getItem("tpa_token");
      const path = pdfMode === "annotated" ? "/api/afsp/download-annotated" : "/api/afsp/download-guide";
      const resp = await fetch(`${API}${path}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) { toast.error("Could not load study guide"); return; }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      setPdfBlobUrl(url);
      revoked = url;
    })();
    return () => { if (revoked) URL.revokeObjectURL(revoked); };
  }, [status, pdfViewerOpen, pdfMode]);

  const jumpTo = (page) => {
    setPdfViewerOpen(true);
    setPdfPage(page || 1);
  };

  if (loading || !status) {
    return <div className="p-10 text-muted-foreground">Loading…</div>;
  }

  if (!status.has_access) {
    return (
      <div className="min-h-screen bg-background grid place-items-center p-6" data-testid="afsp-locked">
        <Card className="max-w-md w-full p-8 text-center">
          <div className="mx-auto h-14 w-14 rounded-full bg-accent/10 text-accent grid place-items-center mb-4">
            <Award size={26} />
          </div>
          <h2 className="font-serif text-3xl mb-2">Unlock AFSP Prep</h2>
          <p className="text-sm text-muted-foreground mb-6">
            The Circular 230 study guide and 18-question AFTR practice exam are part of the AFSP Prep Bundle ($199, one-time).
          </p>
          <Button asChild className="w-full" data-testid="afsp-prep-buy-btn">
            <Link to="/afsp-bundle">See the bundle <ArrowRight size={14} className="ml-2" /></Link>
          </Button>
          <Button asChild variant="ghost" className="w-full mt-2" data-testid="afsp-prep-back-btn">
            <Link to="/dashboard">Back to dashboard</Link>
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background paper-texture" data-testid="afsp-prep-page">
      <header className="border-b border-border bg-card">
        <div className="max-w-5xl mx-auto p-6 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="afsp-brand-link">
            {branding.logo_url ? (
              <img src={branding.logo_url} alt="" className="h-9 w-9 object-contain" />
            ) : (
              <div className="h-9 w-9 rounded-md bg-primary text-primary-foreground grid place-items-center font-serif">
                {branding.brand_name?.[0]}
              </div>
            )}
            <div className="font-serif text-2xl">{branding.brand_name}</div>
          </Link>
          <Button variant="ghost" onClick={() => nav("/dashboard")} data-testid="afsp-nav-dashboard">
            Back to dashboard
          </Button>
        </div>
      </header>

      <div className="max-w-5xl mx-auto p-6 lg:p-12">
        <div className="mb-10">
          <div className="label-caps mb-2 text-primary">AFSP Prep</div>
          <h1 className="font-serif text-4xl md:text-5xl mb-3">AFTR Exam Prep Kit</h1>
          <p className="text-muted-foreground max-w-2xl">
            Download the Circular 230 study guide and take the 18-question practice exam. Unlimited retakes.
          </p>
        </div>

        {/* Progressive Practice Mode banner */}
        <Card className="p-6 mb-8 bg-gradient-to-br from-primary/10 to-accent/10 border-primary/30" data-testid="afsp-exam-sim-cta">
          <div className="flex flex-wrap items-center gap-4">
            <div className="h-14 w-14 rounded-full bg-primary text-primary-foreground grid place-items-center shrink-0 shadow-lg">
              <Award size={26} />
            </div>
            <div className="flex-1 min-w-[220px]">
              <div className="label-caps mb-1 text-primary">New · Progressive Practice Mode</div>
              <h2 className="font-serif text-2xl leading-tight">Full 100-question, 3-hour AFTR simulation</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Randomized order, randomized answers, live countdown, autosubmit at 0:00 — same rules as the real IRS-approved AFTR.
              </p>
            </div>
            <Button asChild data-testid="afsp-open-exam-sim">
              <Link to="/afsp-exam-sim">
                Start full simulation <ArrowRight size={14} className="ml-2" />
              </Link>
            </Button>
          </div>
        </Card>

        {/* PDF + Study companion */}
        <Card className="p-0 md:p-0 mb-8 overflow-hidden" data-testid="afsp-pdf-card">
          <div className="grid md:grid-cols-[300px_1fr]">
            {/* Left: Study companion sidebar */}
            <aside className="border-b md:border-b-0 md:border-r border-border bg-secondary/40 p-5">
              <div className="flex items-center gap-2 mb-3">
                <ListOrdered size={16} className="text-primary" />
                <div className="label-caps">High-yield sections</div>
              </div>
              <p className="text-xs text-muted-foreground mb-4">
                Tap any section to open the annotated study guide at that page — with the key phrases already highlighted.
              </p>
              <div className="space-y-2" data-testid="afsp-study-sections">
                {sections.map((s, i) => (
                  <button
                    key={s.code + i}
                    onClick={() => jumpTo(s.page)}
                    className="w-full text-left p-3 rounded-md border border-border hover:border-primary/50 hover:bg-primary/5 transition-colors group"
                    data-testid={`afsp-jump-${s.code.replace(/[§\s.·]/g, "").toLowerCase()}`}
                  >
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className="font-serif text-sm text-primary">{s.code}</span>
                      {s.page ? (
                        <span className="text-[10px] uppercase tracking-widest text-muted-foreground">p. {s.page}</span>
                      ) : (
                        <span className="text-[10px] uppercase tracking-widest text-accent">study note</span>
                      )}
                    </div>
                    <div className="text-sm font-medium leading-tight">{s.title}</div>
                    <div className="text-[11px] text-muted-foreground mt-1 leading-relaxed line-clamp-3">{s.why}</div>
                  </button>
                ))}
              </div>
            </aside>

            {/* Right: PDF panel */}
            <div className="p-6">
              <div className="flex items-start gap-3 mb-4">
                <div className="h-12 w-12 rounded-md bg-primary/10 text-primary grid place-items-center shrink-0">
                  <FileCheck2 size={22} />
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="font-serif text-2xl">Circular 230 Study Guide</h2>
                  <p className="text-sm text-muted-foreground">
                    The full IRS text. Switch between the clean copy or the pre-highlighted study copy.
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2 mb-4">
                <div className="inline-flex rounded-md border border-border overflow-hidden" role="tablist">
                  <button
                    className={`px-3 py-1.5 text-xs font-medium transition-colors ${pdfMode === "annotated" ? "bg-primary text-primary-foreground" : "bg-transparent hover:bg-secondary"}`}
                    onClick={() => setPdfMode("annotated")}
                    data-testid="afsp-pdf-mode-annotated"
                  >
                    <Highlighter size={12} className="inline mr-1.5" /> Highlighted study copy
                  </button>
                  <button
                    className={`px-3 py-1.5 text-xs font-medium transition-colors ${pdfMode === "plain" ? "bg-primary text-primary-foreground" : "bg-transparent hover:bg-secondary"}`}
                    onClick={() => setPdfMode("plain")}
                    data-testid="afsp-pdf-mode-plain"
                  >
                    <FileCheck2 size={12} className="inline mr-1.5" /> Clean IRS copy
                  </button>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPdfViewerOpen((v) => !v)}
                  disabled={!status.pdf_available}
                  data-testid="afsp-toggle-viewer"
                >
                  {pdfViewerOpen ? "Hide viewer" : "Read inline"}
                </Button>
                <Button size="sm" onClick={() => downloadPdf()} disabled={!status.pdf_available} data-testid="afsp-download-btn">
                  <Download size={12} className="mr-1.5" /> Download PDF
                </Button>
              </div>

              {pdfViewerOpen && pdfBlobUrl && (
                <div className="rounded-md overflow-hidden border border-border" data-testid="afsp-pdf-viewer">
                  <iframe
                    key={`${pdfMode}-${pdfPage}`}
                    src={`${pdfBlobUrl}#page=${pdfPage}&toolbar=1&navpanes=0`}
                    className="w-full"
                    style={{ height: "70vh", minHeight: 520 }}
                    title="Circular 230 Study Guide"
                  />
                </div>
              )}
              {!status.pdf_available && (
                <p className="text-xs text-muted-foreground">Study guide upload pending — check back shortly.</p>
              )}
            </div>
          </div>
        </Card>

        {/* Quiz result banner */}
        {result && (
          <Card
            className={`p-6 md:p-8 mb-8 border-2 ${result.passed ? "border-emerald-500/50 bg-emerald-50/40 dark:bg-emerald-950/20" : "border-amber-500/50 bg-amber-50/40 dark:bg-amber-950/20"}`}
            data-testid="afsp-result-banner"
          >
            <div className="flex flex-wrap items-center gap-4">
              <div className={`h-14 w-14 rounded-full grid place-items-center shrink-0 ${result.passed ? "bg-emerald-500/15 text-emerald-600" : "bg-amber-500/15 text-amber-600"}`}>
                {result.passed ? <Award size={26} /> : <RotateCcw size={26} />}
              </div>
              <div className="flex-1 min-w-[240px]">
                <div className="label-caps mb-1">Practice Exam Result</div>
                <h3 className="font-serif text-3xl">
                  {result.score}/{result.total} correct · {result.percent}%
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                  {result.passed
                    ? "You cleared the 70% AFTR passing threshold. Nice work — grab your certificate below and share it."
                    : "Below the 70% AFTR passing threshold. Review the rationale on each miss below, then retake."}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                {result.passed && (
                  <Button asChild data-testid="afsp-view-cert-btn">
                    <Link to="/afsp-certificate">
                      <Award size={14} className="mr-2" /> View my certificate
                    </Link>
                  </Button>
                )}
                <Button variant="outline" onClick={resetQuiz} data-testid="afsp-retake-btn">
                  <RotateCcw size={14} className="mr-2" /> Retake
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* Existing certificate banner (persistent — visible on load if the user already passed once) */}
        {!result && status.certificate && (
          <Card className="p-6 mb-8 border-2 border-emerald-500/40 bg-emerald-50/30 dark:bg-emerald-950/20 flex flex-wrap items-center gap-4" data-testid="afsp-existing-cert">
            <div className="h-12 w-12 rounded-full bg-emerald-500/15 text-emerald-600 grid place-items-center shrink-0">
              <Award size={22} />
            </div>
            <div className="flex-1 min-w-[220px]">
              <div className="label-caps mb-0.5 text-emerald-700 dark:text-emerald-400">Certificate on file</div>
              <div className="font-serif text-xl">You've already passed with {status.certificate.percent}% — cert ID {status.certificate.cert_id}</div>
            </div>
            <Button asChild variant="outline" data-testid="afsp-open-existing-cert">
              <Link to="/afsp-certificate">Open certificate <ArrowRight size={14} className="ml-2" /></Link>
            </Button>
          </Card>
        )}

        {/* Progress bar (only while attempting, not after submit) */}
        {!result && questions.length > 0 && (
          <div className="mb-6 sticky top-0 z-10 bg-background/95 backdrop-blur-sm py-3 -mx-2 px-2" data-testid="afsp-progress-bar">
            <div className="flex items-center justify-between text-xs text-muted-foreground mb-1.5">
              <span>Progress</span>
              <span>{answered} / {totalQ} answered</span>
            </div>
            <Progress value={pct} className="h-2" />
          </div>
        )}

        {/* Questions */}
        <div className="space-y-4" data-testid="afsp-quiz">
          {questions.map((q, i) => {
            const selected = result ? result.detail[i].selected : answers[q.id];
            const correct = result?.detail[i]?.correct;
            const isCorrect = result?.detail[i]?.is_correct;
            return (
              <Card key={q.id} className="p-6" data-testid={`afsp-question-${i}`}>
                <div className="flex items-baseline gap-2 mb-2">
                  <span className="font-serif text-primary text-2xl">{i + 1}.</span>
                  <span className="text-[10px] uppercase tracking-widest text-muted-foreground">{q.domain}</span>
                </div>
                <p className="font-medium mb-4">{q.prompt}</p>
                <div className="space-y-2">
                  {q.options.map((opt, oi) => {
                    const isSelected = selected === oi;
                    let cls = "border-border hover:border-primary/40";
                    if (result) {
                      if (oi === correct) cls = "border-emerald-500/70 bg-emerald-50 dark:bg-emerald-950/30";
                      else if (isSelected && !isCorrect) cls = "border-red-500/60 bg-red-50 dark:bg-red-950/30";
                    } else if (isSelected) {
                      cls = "border-primary bg-primary/5";
                    }
                    return (
                      <button
                        key={oi}
                        type="button"
                        disabled={!!result}
                        onClick={() => pick(q.id, oi)}
                        className={`w-full text-left p-3 rounded-md border-2 transition-colors flex items-center gap-3 ${cls}`}
                        data-testid={`afsp-q${i}-opt-${oi}`}
                      >
                        <span className={`h-6 w-6 rounded-full grid place-items-center text-xs shrink-0 ${isSelected ? "bg-primary text-primary-foreground" : "bg-secondary"}`}>
                          {String.fromCharCode(65 + oi)}
                        </span>
                        <span className="text-sm">{opt}</span>
                        {result && oi === correct && <Check size={16} className="ml-auto text-emerald-600 shrink-0" />}
                        {result && isSelected && !isCorrect && <X size={16} className="ml-auto text-red-500 shrink-0" />}
                      </button>
                    );
                  })}
                </div>
                {result && (
                  <div className="mt-4 p-3 rounded-md bg-secondary/60 text-sm">
                    <div className="label-caps mb-1 text-primary">Rationale</div>
                    <p className="text-muted-foreground leading-relaxed">{result.detail[i].rationale}</p>
                  </div>
                )}
              </Card>
            );
          })}
        </div>

        {/* Submit */}
        {!result && questions.length > 0 && (
          <div className="mt-8 flex flex-wrap items-center justify-between gap-3">
            <div className="text-sm text-muted-foreground">
              {answered}/{totalQ} answered · {totalQ - answered > 0 ? `${totalQ - answered} left` : "ready to submit"}
            </div>
            <Button
              size="lg"
              onClick={submit}
              disabled={submitting || answered < totalQ}
              data-testid="afsp-submit-btn"
            >
              {submitting ? "Grading…" : "Submit for grading"}
              <ArrowRight size={14} className="ml-2" />
            </Button>
          </div>
        )}

        {questions.length === 0 && status.has_access && (
          <div className="p-10 text-center text-muted-foreground">
            <BookOpen size={28} className="mx-auto mb-3 opacity-60" />
            Loading questions…
          </div>
        )}
      </div>
    </div>
  );
}
